import { useEffect, useState } from "react";
import { freshnessAge, isFeedStalled } from "../../lib/freshness";
import { StatusMark } from "./Status";
import { MicroLabel } from "./Text";

/**
 * The age of what is on screen, ticking.
 *
 * It re-renders once a second, which is the point: a static "updated 4s ago"
 * printed at fetch time is the same lie as no caption at all, because it stops
 * being true one second later. The tick is the only interval timer in the
 * application and it drives a caption, never a fetch.
 *
 * Three states, because they mean three different things to someone watching a
 * station reboot:
 *
 *   - fetching — a request is in flight right now
 *   - live     — the last update arrived within a few poll intervals
 *   - stalled  — nothing has arrived in three intervals, so the figures are old
 *
 * Stalled is the state worth having. React Query suspends polling on a
 * backgrounded tab, so a panel left open on a second monitor is quietly frozen —
 * and that is precisely the page an operator glances at and trusts.
 */

const TICK_MS = 1_000;

export function Freshness({
  updatedAt,
  isFetching,
  intervalMs,
}: {
  /** React Query's `dataUpdatedAt`: 0 until a fetch has succeeded. */
  readonly updatedAt: number;
  readonly isFetching: boolean;
  /** The view's `refetchInterval`, which is what "late" is measured against. */
  readonly intervalMs: number;
}) {
  const now = useTick(TICK_MS);

  const stalled = isFeedStalled(updatedAt, now, intervalMs);
  const age = freshnessAge(updatedAt, now);

  return (
    <span
      className="inline-flex items-center gap-2"
      // Polite, and only ever a few words: the caption changes every second, and a
      // screen reader announcing each tick would make the page unusable. `off` is
      // deliberate — the figures themselves are what a reader follows.
      aria-live="off"
    >
      <StatusMark tone={stalled ? "transitional" : isFetching ? "charging" : "available"} />
      <MicroLabel className={stalled ? "text-state-transitional" : ""}>
        {isFetching ? "Updating" : stalled ? `Not updating · ${age}` : `Updated ${age}`}
      </MicroLabel>
    </span>
  );
}

/**
 * Returns the current time, refreshed on an interval.
 *
 * Its own hook rather than an inline effect so the interval is created once per
 * mounted caption and cleared with it — a leaked one-second timer per navigation
 * would be invisible until the panel had been open an afternoon.
 */
function useTick(everyMs: number): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(timer);
  }, [everyMs]);

  return now;
}
